import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { AuthServerResponse } from "@data/schemas/auth-server/auth-server-response";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { LocalStorageService } from "@core/services/storage/local-storage.service";
import {
	CompleteResponse,
	LoginResponse
} from "@shared/schemas/complete-login";
import {
	EnvironmentConfig,
	ENV_CONFIG
} from "@core/environments/environment-config";
import { ignoreHttpError } from "@core/interceptors/http-error.interceptor";
import { UserStorage } from "@shared/schemas/enums/user-storage";
import { NgxRolesService } from "ngx-permissions";
import { BusinessService } from "@data/services/business.service";
import { ZendeskService } from "../zendesk.service";
import { CookieService } from "ngx-cookie-service";

@Injectable({
	providedIn: "root"
})
export class AsaasService {
	readonly AUTH_ENDPOINT = "auth";

	private userLoggedIn = new BehaviorSubject<LoginResponse | undefined>(
		undefined
	);
	userLoggedIn$ = this.userLoggedIn.asObservable();

	constructor(
		private http: HttpClient,
		private localStorage: LocalStorageService,
		private roles: NgxRolesService,
		private businessService: BusinessService,
		private cookieService: CookieService,
		private zendeskService: ZendeskService,
		@Inject(ENV_CONFIG) private config: EnvironmentConfig
	) {}

	authorizationAuthServerAsaas(): Observable<AuthServerResponse> {
		return this.http.get<AuthServerResponse>(
			`${this.AUTH_ENDPOINT}/asaas-authorization`,
			{
				withCredentials: false
			}
		);
	}

	loginByAsaas(code: string): Observable<CompleteResponse> {
		const params = new HttpParams().set("code", code);

		return this.http
			.get<CompleteResponse>(
				`${this.AUTH_ENDPOINT}/asaas-authentication`,
				{ params, withCredentials: true, context: ignoreHttpError() }
			)
			.pipe(
				tap((response) => {
					this.setLocalStorage(response);
					this.setSession(response.loginResponse);
				})
			);
	}

	private setLocalStorage(response: CompleteResponse) {
		this.localStorage.set("jwtZendesk", response.loginResponse.jwtZendesk);
	}

	private setSession(authResult: LoginResponse) {
		this.localStorage.set<LoginResponse>(UserStorage.UserLogin, authResult);
		if (authResult.hashedFingerprint) {
			this.localStorage.set("fingerprint", authResult.hashedFingerprint);
		}
		this.userLoggedIn.next(authResult);
	}

	logoutLegacy(): Observable<unknown> {
		const urlBaseErp = this.config.environment.PROXY_BASEERP;
		return this.http.delete(
			`${urlBaseErp}/autenticacao/?base=false&token=${this.getToken()}`
		);
	}

	logout(): Observable<unknown> {
		return this.http
			.post(
				`${this.config.environment.API_BFF}/auth/logout`,
				{
					base: "true"
				},
				{ withCredentials: true }
			)
			.pipe(
				tap(() => {
					this.localStorage.remove(UserStorage.UserLogin);
					this.localStorage.remove("jwtZendesk");
					this.roles.flushRolesAndPermissions();
					this.businessService.clearBusinessData();
					this.zendeskService.hideZendesk();
				})
			);
	}

	getToken(): string {
		return (
			this.localStorage.get<LoginResponse>(UserStorage.UserLogin)
				?.access_token ?? ""
		);
	}
}
